<template>
  <div :id="`recipe_${recipe.id}`" class="w-full">
    <Head :title="recipe.title">
      <meta
        head-key="description"
        name="description"
        :content="recipe.ingress || `Se recept på ${recipe.title}.`"
      />
    </Head>
    <!-- Admin controls -->
    <AdminControls
      :current-user="currentUser"
      :recipes-path="recipesPath"
      :edit-recipe-path="editRecipePath"
      :recipe-path="recipePath"
      :csrf-token="csrfToken"
    />

    <!-- MOBILE HERO -->
    <div class="md:hidden">
      <div
        class="relative h-[320px] overflow-hidden select-none"
        @mousedown="onDragStart"
        @mousemove="onDragMove"
        @mouseup="onDragEnd"
        @mouseleave="onDragEnd"
        @touchstart.passive="onDragStart"
        @touchmove.passive="onDragMove"
        @touchend="onDragEnd"
      >
        <div
          class="flex h-full transition-transform duration-300 ease-in-out"
          :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
        >
          <img
            v-for="(img, i) in allImages"
            :key="i"
            :src="img.mobile || img.xlarge"
            :alt="recipe.title"
            class="w-full h-full object-cover shrink-0 pointer-events-none"
            :loading="i === 0 ? 'eager' : 'lazy'"
          />
        </div>
        <!-- Gradient + dots -->
        <template v-if="allImages.length > 1">
          <div
            class="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"
          />
          <div
            class="absolute bottom-3 left-0 right-0 flex justify-center gap-2"
          >
            <button
              v-for="(img, i) in allImages"
              :key="i"
              class="w-2 h-2 rounded-full transition-colors"
              :class="activeIndex === i ? 'bg-white' : 'bg-white/40'"
              @click="activeIndex = i"
            />
          </div>
        </template>
      </div>

      <div class="px-6 pt-7 pb-2 flex flex-col gap-5">
        <!-- Title -->
        <h1
          class="font-[Poppins] font-extrabold text-[42px] text-[#191c1e] tracking-[-1.2px] leading-[1.2] m-0"
        >
          {{ recipe.title }}
        </h1>

        <!-- Description -->
        <p
          v-if="recipe.ingress"
          class="font-[Plus_Jakarta_Sans] text-lg text-[#3c4947] leading-[30px]"
        >
          {{ recipe.ingress }}
        </p>

        <!-- Stats -->
        <div class="flex gap-8 pt-2 border-t border-[#eceef0]">
          <div v-if="totalTime">
            <p
              class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
            >
              TID
            </p>
            <p
              class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0"
            >
              {{ totalTime }} min
            </p>
          </div>
          <div v-if="recipe.difficulty">
            <p
              class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
            >
              SVÅRIGHETSGRAD
            </p>
            <p
              class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0 capitalize"
            >
              {{ recipe.difficulty }}
            </p>
          </div>
          <div v-if="recipe.servings">
            <p
              class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
            >
              ANTAL
            </p>
            <p
              class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0"
            >
              {{ recipe.servings }} {{ recipe.servings_unit || "portioner" }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- DESKTOP HERO -->
    <div class="hidden md:block max-w-[1280px] mx-auto px-6 pt-8 pb-0">
      <!-- Breadcrumb -->
      <div class="flex items-center gap-2 mb-8">
        <Link
          :href="recipesPath"
          class="font-[Poppins] font-bold text-sm text-teal-500 tracking-[1.4px] uppercase no-underline"
          >Recept</Link
        >
        <template v-for="(cat, i) in recipe.categories" :key="cat.id">
          <span class="font-[Poppins] font-bold text-sm text-teal-500">/</span>
          <Link
            :href="cat.path ?? recipesPath"
            class="font-[Poppins] font-bold text-sm text-teal-500 tracking-[1.4px] uppercase no-underline"
            >{{ cat.name }}</Link
          >
        </template>
        <span class="font-[Poppins] font-bold text-sm text-teal-500">/</span>
        <span
          class="font-[Poppins] text-sm text-[#191c1e] tracking-[1.4px] uppercase"
          >{{ recipe.title }}</span
        >
      </div>

      <div class="grid grid-cols-12 gap-12 items-start">
        <!-- Text -->
        <div class="col-span-5 flex flex-col gap-6">
          <h1
            class="font-[Poppins] font-extrabold text-[clamp(28px,4vw,48px)] text-[#191c1e] tracking-[-1px] leading-[1.2] m-0"
          >
            {{ recipe.title }}
          </h1>

          <p
            v-if="recipe.ingress"
            class="font-[Plus_Jakarta_Sans] text-lg text-[#3c4947] leading-[30px] max-w-[440px]"
          >
            {{ recipe.ingress }}
          </p>

          <div class="flex gap-8 pt-4 border-t border-[#eceef0] flex-wrap">
            <div v-if="totalTime">
              <p
                class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
              >
                TID
              </p>
              <p
                class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0"
              >
                {{ totalTime }} min
              </p>
            </div>
            <div v-if="recipe.difficulty">
              <p
                class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
              >
                SVÅRIGHETSGRAD
              </p>
              <p
                class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0 capitalize"
              >
                {{ recipe.difficulty }}
              </p>
            </div>
            <div v-if="recipe.servings">
              <p
                class="font-[Poppins] font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase mb-0.5"
              >
                ANTAL
              </p>
              <p
                class="font-[Plus_Jakarta_Sans] font-bold text-lg text-[#191c1e] m-0"
              >
                {{ recipe.servings }} {{ recipe.servings_unit || "portioner" }}
              </p>
            </div>
          </div>
        </div>

        <!-- Image -->
        <div class="col-span-7">
          <div
            class="relative overflow-hidden rounded-[48px] h-[572px] shadow-2xl select-none"
            @mousedown="onDragStart"
            @mousemove="onDragMove"
            @mouseup="onDragEnd"
            @mouseleave="onDragEnd"
            @touchstart.passive="onDragStart"
            @touchmove.passive="onDragMove"
            @touchend="onDragEnd"
          >
            <div
              class="flex h-full transition-transform duration-300 ease-in-out"
              :style="{ transform: `translateX(-${activeIndex * 100}%)` }"
            >
              <img
                v-for="(img, i) in allImages"
                :key="i"
                :src="img.xlarge"
                :alt="recipe.title"
                class="w-full h-full object-cover shrink-0 pointer-events-none"
                :loading="i === 0 ? 'eager' : 'lazy'"
              />
            </div>
            <!-- Gradient + dots -->
            <template v-if="allImages.length > 1">
              <div
                class="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"
              />
              <div
                class="absolute bottom-5 left-0 right-0 flex justify-center gap-2"
              >
                <button
                  v-for="(img, i) in allImages"
                  :key="i"
                  class="w-2.5 h-2.5 rounded-full transition-colors"
                  :class="activeIndex === i ? 'bg-white' : 'bg-white/40'"
                  @click="activeIndex = i"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- INGREDIENTS + STEPS -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16">
      <div class="flex flex-col md:grid md:grid-cols-12 md:gap-12">
        <!-- Ingredients card -->
        <div class="md:col-span-4 mb-10 md:mb-0">
          <div
            class="rounded-[32px] md:rounded-[40px] p-8 md:p-10 md:sticky md:top-24 bg-white border-2 border-teal-500/20 shadow-sm"
          >
            <h2
              class="font-[Poppins] font-bold text-[30px] text-[#191c1e] mb-6"
            >
              Ingredienser
            </h2>

            <div class="flex flex-col gap-4">
              <div v-for="(groups, title) in groupedIngredients" :key="title">
                <!-- Group title if not the first/default group -->
                <p
                  v-if="Object.keys(groupedIngredients).length > 1"
                  class="font-[Poppins] font-bold text-xs text-[#3c4947] tracking-[1.2px] uppercase mb-4"
                >
                  {{ title }}
                </p>
                <ul class="list-none flex flex-col">
                  <template v-for="group in groups" :key="group.id">
                    <li
                      v-for="ingredient in group.ingredients"
                      :key="ingredient.id"
                      class="flex items-start gap-3 text-lg text-[#191c1e] leading-7 pr-3 pl-4 py-3 rounded-lg odd:bg-teal-500/5"
                    >
                      <span
                        class="mt-[0.55em] w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0"
                      ></span>
                      <span class="min-w-0">
                        <strong
                          v-if="ingredientUnit(ingredient)"
                          class="mr-2"
                          >{{ ingredientUnit(ingredient) }}</strong
                        >{{ ingredient.name }}
                      </span>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Steps column -->
        <div class="md:col-span-8">
          <h2
            class="pt-4 border-t border-[#eceef0] mb-10 md:mb-12 font-[Poppins] font-bold text-[clamp(28px,3vw,36px)] text-[#191c1e]"
          >
            Så här gör du
          </h2>

          <!-- Steps -->
          <div class="flex flex-col gap-14 md:gap-16">
            <div v-for="(step, idx) in recipe.steps" :key="step.id">
              <div class="flex gap-3 md:gap-8 items-start">
                <!-- Big faded number -->
                <div class="shrink-0 opacity-20">
                  <span
                    class="font-[Poppins] font-extrabold text-[72px] text-teal-500 leading-[72px]"
                  >
                    {{ String(idx + 1).padStart(2, "0") }}
                  </span>
                </div>

                <div class="flex flex-col gap-3 pt-1 min-w-0 flex-1">
                  <h3
                    class="font-[Poppins] font-bold text-2xl text-[#191c1e] m-0 leading-8"
                  >
                    {{ step.title }}
                  </h3>
                  <div
                    class="font-[Plus_Jakarta_Sans] text-xl text-[#3c4947] leading-[32.5px] m-0"
                    v-html="step.instructions"
                  ></div>

                  <!-- Mobile: inline ingredients -->
                  <div
                    v-if="step.ingredient_groups.length"
                    class="md:hidden rounded-[10px] p-3 bg-[#f2f4f6]"
                  >
                    <p
                      class="font-[Plus_Jakarta_Sans] font-bold text-sm text-[#006b5f] mb-1"
                    >
                      ANVÄND NU:
                    </p>
                    <ul class="list-disc pl-5 m-0">
                      <template
                        v-for="group in step.ingredient_groups"
                        :key="group.id"
                      >
                        <li
                          v-for="ingredient in group.ingredients"
                          :key="ingredient.id"
                          class="font-[Plus_Jakarta_Sans] text-[15px] text-[#191c1e] leading-[1.8]"
                        >
                          {{ formatIngredient(ingredient) }}
                        </li>
                      </template>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Body/tips panel — shown after the configured step -->
              <div
                v-if="recipe.body && recipe.body_after_step === idx"
                class="relative overflow-hidden mt-10 rounded-[32px] md:rounded-[48px] bg-teal-500/5 border border-teal-500/10 p-[clamp(32px,6vw,49px)]"
              >
                <!-- Decorative light-bulb -->
                <div
                  class="absolute bottom-5 right-5 pointer-events-none opacity-10"
                >
                  <Lightbulb
                    :size="96"
                    class="text-teal-500"
                    :stroke-width="1.5"
                  />
                </div>

                <div class="flex items-center gap-3 mb-5">
                  <Lightbulb :size="22" class="text-teal-500" />
                  <span
                    class="font-[Poppins] font-bold text-[30px] text-teal-500"
                  >
                    Emmas tips
                  </span>
                </div>

                <div
                  class="font-[Plus_Jakarta_Sans] font-medium text-xl text-[#3c4947] leading-[32.5px] max-w-[680px] whitespace-pre-line"
                >
                  {{ recipe.body }}
                </div>
              </div>
            </div>
          </div>

          <!-- Interaction bar -->
          <div
            class="mt-12 md:mt-16 rounded-[32px] md:rounded-[40px] bg-[#f2f4f6] p-[clamp(28px,5vw,40px)]"
          >
            <div class="flex flex-col gap-4">
              <div>
                <h4
                  class="font-[Poppins] font-bold text-2xl text-[#191c1e] mb-2"
                >
                  Gillar du receptet?
                </h4>
                <p
                  class="font-[Plus_Jakarta_Sans] text-lg text-[#3c4947] leading-7 m-0 max-w-[340px]"
                >
                  Hjälp andra att hitta hit genom att dela eller spara.
                </p>
              </div>
              <div class="flex gap-4 mt-2">
                <button
                  class="flex items-center justify-center rounded-full bg-white w-14 h-14 shadow-sm transition-shadow hover:shadow-md"
                  @click="shareRecipe"
                >
                  <Share2 :size="20" class="text-teal-500" />
                </button>
                <button
                  class="flex items-center justify-center rounded-full bg-white w-14 h-14 shadow-sm transition-shadow hover:shadow-md"
                  @click="toggleLike"
                >
                  <Heart
                    :size="20"
                    class="text-teal-500"
                    :fill="isLiked ? '#14b8a6' : 'none'"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- LINKED TECHNIQUES & TOOLS -->
    <section
      v-if="linkedResources?.length"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-16"
    >
      <h2
        class="font-[Poppins] font-bold text-[clamp(24px,3vw,36px)] text-[#191c1e] m-0 mb-6 md:mb-8"
      >
        Relaterade tekniker och verktyg
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <a
          v-for="resource in linkedResources"
          :key="resource.path"
          :href="resource.path"
          class="flex gap-4 items-center rounded-2xl bg-white border border-[#eceef0] p-4 hover:border-teal-200 hover:shadow-sm transition-all no-underline"
        >
          <div
            class="overflow-hidden rounded-xl shrink-0"
            style="width: 64px; height: 64px"
          >
            <img
              v-if="resource.heroImageUrl"
              :src="resource.heroImageUrl"
              :alt="resource.label"
              class="w-full h-full object-cover"
              loading="lazy"
            />
            <div
              v-else
              class="w-full h-full bg-teal-50 flex items-center justify-center"
            >
              <Calculator :size="24" class="text-teal-400" />
            </div>
          </div>
          <div class="flex-1">
            <p
              class="font-[Poppins] font-bold text-[15px] text-[#191c1e] m-0 mb-0.5"
            >
              {{ resource.label }}
            </p>
            <p class="text-[13px] text-[#6c7a77] m-0">
              {{ resource.type === "tool" ? "Verktyg" : "Teknik" }}
            </p>
          </div>
          <ArrowRight :size="16" class="text-teal-500 shrink-0" />
        </a>
      </div>
    </section>

    <!-- RELATED RECIPES -->
    <section
      v-if="relatedRecipes.length"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16 md:pb-20"
    >
      <div class="flex items-start justify-between mb-6 md:mb-8">
        <h2
          class="font-[Poppins] font-bold text-[clamp(24px,3vw,36px)] text-[#191c1e] m-0"
        >
          Fler smaker att utforska
        </h2>
        <Link
          :href="recipesPath"
          class="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0 ml-4 no-underline"
        >
          <span class="font-[Poppins] font-bold text-base text-teal-500">
            Visa alla recept
          </span>
          <ArrowRight :size="16" class="text-teal-500" />
        </Link>
      </div>

      <div class="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
        <RecipeCard
          v-for="r in relatedRecipes"
          :key="r.id"
          :recipe="r"
          :current-user="currentUser"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { Head, Link, useForm, router, usePage } from "@inertiajs/vue3";
import {
  Share2,
  Heart,
  Lightbulb,
  ArrowRight,
  BookOpen,
  Calculator,
} from "lucide-vue-next";
import { toast } from "vue-sonner";
import AdminControls from "@/components/recipe/AdminControls.vue";
import RecipeCard from "@/components/recipe/RecipeCard.vue";

const activeIndex = ref(0);

const allImages = computed(() => {
  const imgs = [];
  if (props.recipe.cover_image?.xlarge) imgs.push(props.recipe.cover_image);
  if (props.recipe.images?.length) imgs.push(...props.recipe.images);
  return imgs;
});

const activeImage = computed(
  () => allImages.value[activeIndex.value] || props.recipe.cover_image,
);

const props = defineProps({
  recipe: Object,
  relatedRecipes: { type: Array, default: () => [] },
  linkedResources: { type: Array, default: () => [] },
  currentUser: Object,
  csrfToken: String,
  recipesPath: String,
  recipePath: String,
  editRecipePath: String,
  likePath: String,
  unlikePath: String,
});

const jsonLd = computed(() => {
  const r = props.recipe;
  const allIngredients = r.ingredient_groups.flatMap((g) =>
    g.ingredients.map((i) => {
      const parts = [i.amount, i.amount_type, i.name].filter(Boolean);
      return parts.join(" ");
    }),
  );

  const instructions = r.steps.map((s) => ({
    "@type": "HowToStep",
    name: s.title || "",
    text: s.instructions || "",
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: r.title,
    description: r.ingress || "",
    author: { "@type": "Person", name: "Emma" },
    recipeIngredient: allIngredients,
    recipeInstructions: instructions,
  };
  if (r.cover_image?.xlarge) schema.image = [r.cover_image.xlarge];
  if (r.prep_time) schema.prepTime = `PT${r.prep_time}M`;
  if (r.cook_time) schema.cookTime = `PT${r.cook_time}M`;
  if (r.servings)
    schema.recipeYield = `${r.servings} ${r.servings_unit || "portioner"}`;
  return JSON.stringify(schema);
});

onMounted(() => {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "recipe-jsonld";
  script.textContent = jsonLd.value;
  document.head.appendChild(script);
});

onUnmounted(() => {
  document.getElementById("recipe-jsonld")?.remove();
});

const likeForm = useForm({});
const unlikeForm = useForm({});

const isLiked = computed(() => {
  if (!props.currentUser) return false;
  return props.currentUser.liked_recipe_ids.includes(props.recipe.id);
});

const totalTime = computed(() => {
  const prep = props.recipe.prep_time || 0;
  const cook = props.recipe.cook_time || 0;
  return prep + cook || null;
});

const groupedIngredients = computed(() => {
  const groups = {};
  for (const group of props.recipe.ingredient_groups) {
    const title = group.title || "Ingredienser";
    if (!groups[title]) groups[title] = [];
    groups[title].push(group);
  }
  return groups;
});

function formatIngredient(ingredient) {
  const amount = ingredient.amount
    ? parseFloat(ingredient.amount).toString()
    : "";
  const type = ingredient.amount_type || "";
  const name = ingredient.name || "";
  return [amount, type, name].filter(Boolean).join(" ");
}

function ingredientUnit(ingredient) {
  const amount = ingredient.amount
    ? parseFloat(ingredient.amount).toString()
    : "";
  const type = ingredient.amount_type || "";
  return [amount, type].filter(Boolean).join(" ");
}

const dragStartX = ref(null);

function onDragStart(e) {
  dragStartX.value = e.touches ? e.touches[0].clientX : e.clientX;
}

function onDragMove(e) {
  // prevent scroll interference on touch only if dragging
}

function onDragEnd(e) {
  if (dragStartX.value === null || allImages.value.length < 2) {
    dragStartX.value = null;
    return;
  }
  const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
  const delta = endX - dragStartX.value;
  if (Math.abs(delta) > 40) {
    if (delta < 0) {
      activeIndex.value = (activeIndex.value + 1) % allImages.value.length;
    } else {
      activeIndex.value =
        (activeIndex.value - 1 + allImages.value.length) %
        allImages.value.length;
    }
  }
  dragStartX.value = null;
}

const page = usePage();

function toggleLike() {
  if (!props.currentUser) {
    const newUserPath = page.props.navData?.newUserPath ?? "/users/new";
    router.visit(
      `${newUserPath}?return_to=/users/recipes&like_recipe_id=${props.recipe.id}`,
    );
    return;
  }
  if (isLiked.value) {
    unlikeForm.delete(props.unlikePath, { preserveScroll: true });
  } else {
    likeForm.post(props.likePath, { preserveScroll: true });
  }
}

function shareRecipe() {
  navigator.clipboard
    .writeText(window.location.href)
    .then(() => {
      toast.success("Länk kopierad!", {
        description: "Receptets URL har kopierats till urklipp.",
      });
    })
    .catch(() => {
      toast.error("Kunde inte kopiera länken.");
    });
}
</script>
